// MUI
import {
  ContentCopyOutlined,
  DeleteOutline,
  LinkOutlined,
  OpenInFullOutlined,
  OpenInNew,
} from "@mui/icons-material";

// Third party imports
import i18n from "i18n/config";

// Redux
import { setSnackbar } from "redux/slices/snackbarSlice";

// Ready-made menu items. They are plain objects built when the menu opens, so labels follow the
// current language. Every factory takes `overrides` merged last ({ label, shortcut, hidden... }).

export const divider = Object.freeze({ divider: true });

// "/123/subtask/9" -> "https://crm.../123/subtask/9"
export const toAbsoluteUrl = (url) => {
  if (!url) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const copyToClipboard = async (text) => {
  const value = String(text ?? "");
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // falls back below (http, iframe without permission)
  }
  try {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const done = document.execCommand("copy");
    document.body.removeChild(area);
    return done;
  } catch {
    return false;
  }
};

const notifyCopy = (dispatch, ok, successMessage) =>
  dispatch?.(
    setSnackbar({
      open: true,
      message: ok ? successMessage : i18n.t("ui:contextMenu.copyFailed"),
      severity: ok ? "success" : "error",
    })
  );

/**
 * "Apri". `target` is a function (called with the menu ctx) or an in-app path to navigate to.
 */
export const openItem = (target, overrides = {}) => ({
  id: "open",
  label: i18n.t("ui:contextMenu.open"),
  icon: OpenInFullOutlined,
  hidden: !target,
  onClick: (ctx) => (typeof target === "function" ? target(ctx) : ctx.navigate(target)),
  ...overrides,
});

/** "Apri in nuova scheda" for an in-app path or a full URL. */
export const openInNewTabItem = (url, overrides = {}) => ({
  id: "open-new-tab",
  label: i18n.t("ui:contextMenu.openInNewTab"),
  icon: OpenInNew,
  hidden: !url,
  onClick: () => {
    window.open(toAbsoluteUrl(url), "_blank", "noopener,noreferrer");
  },
  ...overrides,
});

/** "Copia link": copies the absolute URL and confirms with a snackbar. */
export const copyLinkItem = (url, overrides = {}) => ({
  id: "copy-link",
  label: i18n.t("ui:contextMenu.copyLink"),
  icon: LinkOutlined,
  hidden: !url,
  onClick: async (ctx) => {
    const ok = await copyToClipboard(toAbsoluteUrl(url));
    notifyCopy(ctx.dispatch, ok, i18n.t("ui:contextMenu.linkCopied"));
  },
  ...overrides,
});

/** "Copia <label>": copies any text ("Copia email", "Copia nome"). label is already translated. */
export const copyTextItem = (label, text, overrides = {}) => ({
  id: `copy-${label}`,
  label: label || i18n.t("ui:contextMenu.copy"),
  icon: ContentCopyOutlined,
  hidden: text === undefined || text === null || text === "",
  onClick: async (ctx) => {
    const ok = await copyToClipboard(text);
    notifyCopy(ctx.dispatch, ok, i18n.t("ui:contextMenu.copied"));
  },
  ...overrides,
});

/** "Elimina", red. onClick usually opens the existing confirmation dialog. */
export const deleteItem = (onClick, overrides = {}) => ({
  id: "delete",
  label: i18n.t("ui:contextMenu.delete"),
  icon: DeleteOutline,
  danger: true,
  hidden: typeof onClick !== "function",
  onClick,
  ...overrides,
});
